import { useCallback, useRef, useState } from "react";
import apiCoreAdapter from "../../../../hooks/apiCoreAdapter";

export interface SearchablePatient {
  processId: string;
  name: string;
  email: string;
  lastSessionDate: string | null;
  nextSessionDate: string | null;
  /** `process_patient.status` crudo (`lead`/`active`/`on_hold`/`inactive`/`transferred`/…).
   *  Solo se usa para decidir si el email es visible — ver `canShowPatientEmail`. */
  patientStatus: string | null;
}

// El tipo de retorno declarado en apiCoreAdapter.patientsInformation no incluye `email` ni
// `patient_status`, pero el backend sí los devuelve (ver psiplatform-be patients.service.ts,
// getProcessSessionsData). Mismo cast local que ya usa usePatientsData.ts (RawPatientResult)
// para el mismo endpoint.
interface RawSearchPatientResult {
  id: string;
  process_id?: string | null;
  name?: string | null;
  surname?: string | null;
  email?: string | null;
  last_session_date?: string | null;
  next_session_date?: string | null;
  patient_status?: string | null;
}

/**
 * Mismo criterio que `PatientTable.tsx` (tab "Todos"): el email solo se muestra para
 * pacientes Activos — para el resto de los estados (Nuevo, En pausa, Inactivo, Derivación
 * exitosa) no aporta valor de seguimiento y se oculta. No afecta la búsqueda en sí (se sigue
 * pudiendo encontrar un paciente por su email aunque no esté Activo), solo si se lo muestra
 * en el resultado.
 */
export const canShowPatientEmail = (patientStatus: string | null): boolean => patientStatus === "active";

interface UsePatientSearchIndexResult {
  patients: SearchablePatient[];
  loading: boolean;
  error: boolean;
  /** Dispara la carga la primera vez que se llama; llamadas siguientes son no-op mientras haya datos. */
  ensureLoaded: () => void;
}

/**
 * Índice de búsqueda de pacientes para la Agenda: nombre, mail y fechas de última/próxima
 * sesión en un solo fetch (`patientsInformation`, per_page alto para traer todo el listado).
 * Carga perezosa (recién al abrir el buscador) para no sumar una llamada de red al montar
 * la Agenda si nunca se usa.
 */
export function usePatientSearchIndex(): UsePatientSearchIndexResult {
  const [patients, setPatients] = useState<SearchablePatient[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const loadedRef = useRef(false);

  const ensureLoaded = useCallback(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    setLoading(true);
    setError(false);
    apiCoreAdapter
      .patientsInformation({ page: 1, per_page: 800 })
      .then((res) => {
        const raw = (Array.isArray(res?.results) ? res.results : []) as RawSearchPatientResult[];
        const mapped = raw
          .map((r) => ({
            processId: r.process_id || r.id,
            name: [r.name, r.surname].filter(Boolean).join(" ").trim(),
            email: (r.email || "").toLowerCase().trim(),
            lastSessionDate: r.last_session_date || null,
            nextSessionDate: r.next_session_date || null,
            patientStatus: r.patient_status || null,
          }))
          .filter((p) => p.name || p.email);
        setPatients(mapped);
      })
      .catch(() => {
        setError(true);
        // Permite reintentar en la próxima apertura del buscador.
        loadedRef.current = false;
      })
      .finally(() => setLoading(false));
  }, []);

  return { patients, loading, error, ensureLoaded };
}
